import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { getTags, type Tag, type TagsListParams } from '@/lib/tagsApi'

interface UseTagsResult {
  tags: Tag[]
  isLoading: boolean
  error: string | null
  total: number
  refresh: () => void
}

export function useTags(params: TagsListParams = {}): UseTagsResult {
  const [tags, setTags] = useState<Tag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)

  // Use ref to store params for the fetch function to avoid dependency issues
  const paramsRef = useRef(params)
  paramsRef.current = params

  // Stable params key - only recalculates when actual param values change
  const paramsKey = useMemo(() => JSON.stringify(params), [params])

  // Memoized fetch function - stable reference, reads params from ref
  const fetchTags = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await getTags(paramsRef.current)
      setTags(response.data)
      setTotal(response.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setIsLoading(false)
    }
  }, []) // No dependencies - uses ref for params

  // Fetch when paramsKey changes
  useEffect(() => {
    fetchTags()
  }, [paramsKey, fetchTags])

  return {
    tags,
    isLoading,
    error,
    total,
    refresh: fetchTags,
  }
}
